// Anmeldelser fra kunderne: 3 dage efter en færdig booking får kunden én mail (og evt. SMS) "Hvordan gik det?" med 1–5
// stjerner som links. Et klik åbner en offentlig side uden login (#/anmeld/<token>/<stjerner>), hvor kunden bekræfter
// stjernerne og kan skrive en kommentar. Anmeldelsen gemmes i Freemi (tabellen kundeanmeldelser).
// Lovligt (Trustpilots retningslinjer og markedsføringsloven § 3 om vildledning):
//  • INGEN "review gating": ALLE kunder – uanset antal stjerner – får tilbudt at dele deres oplevelse på brandets egen
//    Trustpilot-side (hovedbrandet: virksomhedens; har et brand ingen side, tilbydes Trustpilot ikke). Kun glade kunder må aldrig
//    sendes videre alene.
//  • 1–3 stjerner får derudover "Vil du have, at vi kontakter dig?" → en kundesag i Kundecenter og en notifikation til kontoret.
//  • Anmodningen er sin egen type ('anmeldelse', se samtykke.ts): den handler om en ydelse, kunden har købt, så den kræver
//    ikke markedsføringssamtykke – men en kunde, der har afmeldt sig, får den ikke, og mailen har afmeldingslinket i bunden.
//  • Højst én anmodning pr. kunde pr. 90 dage. Udsendelsen respekterer altid klienter.udsendelse_spaerret.
// Bruges ens i testversionen (src/app/api/demo.ts), i drift (automationMotor.ts) og på siden.
import { fejl } from './fejl.ts';

export const ANMELD = {
  karensDage: 90,        // højst én anmodning pr. kunde pr. 90 dage
  lavMax: 3,             // 1–3 stjerner: "Vil du have, at vi kontakter dig?"
  liveMin: 10,           // live-beskeden "4,8 ★ fra n anmeldelser" vises først fra 10 anmeldelser
  aabenDage: 30,         // kunden kan give/rette sin anmeldelse i 30 dage efter anmodningen
  kommentarMax: 2000,
  svarMax: 2000,
} as const;

export type Stjerner = 1 | 2 | 3 | 4 | 5;
export const gyldigeStjerner = (n: unknown): n is Stjerner => typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= 5;
/** Tokenet i linket: 24 tegn 0-9a-f (96 bit tilfældigt, sat af databasen på bookingen – bookinger.anmeld_token). */
export const gyldigtAnmeldToken = (t: unknown): t is string => typeof t === 'string' && /^[0-9a-f]{24}$/.test(t);
export const erLav = (s: Stjerner) => s <= ANMELD.lavMax;

/** Linket til anmeldelsessiden på brandets (eller virksomhedens) adresse, fx https://steamfox.dk/#/anmeld/<token>. */
export const anmeldLink = (adresse: string, token: string) => `${adresse.replace(/\/?$/, '/')}#/anmeld/${token}`;

/** Fem linjer med stjerner som links – mailen viser dem som en række stjerner (mail.ts), tekstversionen som de står. */
export function stjerneLinjer(link: string): string {
  if (!link) return '';
  return ([5, 4, 3, 2, 1] as const).map((n) => `${'★'.repeat(n)}${'☆'.repeat(5 - n)} ${STJERNE_ORD[n]}: ${link}/${n}`).join('\n');
}
export const STJERNE_ORD: Record<Stjerner, string> = { 1: 'Dårlig', 2: 'Under middel', 3: 'OK', 4: 'God', 5: 'Fremragende' };
/** Genkender en linje fra stjerneLinjer (mail.ts tegner dem som knapper). */
export const STJERNE_LINJE = /^(★{1,5})☆{0,4}\s+([^:]{1,30}):\s+(https?:\/\/\S+\/anmeld\/[0-9a-f]{24}\/([1-5]))$/;

/** Hvad kunden får tilbudt efter at have givet sine stjerner. Trustpilot tilbydes ALTID (ingen review gating) –
 *  stjernerne afgør kun, om kunden også får tilbudt, at vi kontakter dem. */
export function efterAnmeldelse(stjerner: Stjerner, trustpilot: string | null | undefined): { trustpilot: string | null; tilbydKontakt: boolean } {
  return { trustpilot: trustpilotUrl(trustpilot), tilbydKontakt: erLav(stjerner) };
}
/** Kun en rigtig https-adresse bruges som Trustpilot-link. */
export function trustpilotUrl(u: string | null | undefined): string | null {
  const s = (u ?? '').trim();
  return /^https:\/\/[^\s"<>]+$/.test(s) ? s : null;
}
/** Trustpilot-siden for bookingens brand: et brand (fx SteamFox) bruger KUN sin egen side – har brandet ingen, vises intet
 *  Trustpilot-tilbud (siden takker blot). Hovedbrandet bruger virksomhedens (Indstillinger › Kundecenter og Trustpilot).
 *  Reglen er pr. brand – aldrig pr. stjerneantal (ingen gating). */
export const trustpilotFor = (brand: { trustpilot: string } | null | undefined, klient: string | null | undefined) => trustpilotUrl(brand ? brand.trustpilot : klient);

/** Må kunden få en anmodning nu? Ikke hvis kunden har fået én (for en anden booking) inden for de sidste 90 dage. */
export function inden90(sidste: string | null | undefined, nu = Date.now()): boolean {
  if (!sidste) return false;
  const t = Date.parse(sidste);
  return Number.isFinite(t) && nu - t < ANMELD.karensDage * 86_400_000;
}
export const INDEN_90 = 'Kunden har fået en anmodning om anmeldelse inden for 90 dage';
export const AFMELDT = 'Kunden har afmeldt sig';
export const INTET_LINK = 'Bookingen har intet anmeldelseslink';

/** En anmeldelse, som kontoret ser den (Indsigt › Anmeldelser). stjerner = null: anmodet, men ikke besvaret endnu. */
export type KundeAnmeldelse = {
  id: number; booking_id: number; kunde: string; kunde_id: string | null; brand_id: string | null; brand: string;
  steamer_id: string | null; steamer: string; ydelse: string; dato: string | null;
  sendt: string; besvaret: string | null; stjerner: Stjerner | null; kommentar: string; kontakt: boolean; kundesag_id: number | null;
  svar: string; svar_tid: string | null; svar_af: string | null; notat: string;
};

/** Den offentlige side: kun det, kunden skal se (ingen andre kunders data). */
export type AnmeldSide = {
  firma: string; logo: string | null; primaer: string | null; ydelse: string; dato: string | null; steamer: string;
  stjerner: Stjerner | null; kommentar: string; kontakt: boolean; besvaret: boolean; trustpilot: string | null; aaben: boolean;
};
export type AnmeldInput = { stjerner: Stjerner; kommentar: string; kontakt: boolean };

/** Tjek og rens kundens anmeldelse. Kontakt kan kun vælges ved 1–3 stjerner. */
export function tjekAnmeld(i: Partial<AnmeldInput>): AnmeldInput {
  if (!gyldigeStjerner(i.stjerner)) throw fejl('Vælg 1–5 stjerner.', { stjerner: 'Vælg 1–5 stjerner.' });
  const kommentar = String(i.kommentar ?? '').replace(/\r/g, '').replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, '').trim().slice(0, ANMELD.kommentarMax);
  return { stjerner: i.stjerner, kommentar, kontakt: erLav(i.stjerner) && i.kontakt === true };
}

/** Beskrivelsen på kundesagen, når kunden vil kontaktes. */
export function kundesagTekst(stjerner: Stjerner, kommentar: string): string {
  return `Anmeldelse: ${stjerner} af 5 stjerner. Kunden vil gerne kontaktes.${kommentar ? `\n\n"${kommentar}"` : ''}`.slice(0, 3000);
}

/** Gennemsnit (én decimal) og fordeling (antal pr. 1–5 stjerner) for de besvarede anmeldelser. */
export function anmeldStatistik(l: { stjerner: Stjerner | null }[]): { antal: number; snit: number | null; fordeling: [number, number, number, number, number] } {
  const f: [number, number, number, number, number] = [0, 0, 0, 0, 0];
  let sum = 0, antal = 0;
  for (const a of l) if (a.stjerner) { f[a.stjerner - 1]++; sum += a.stjerner; antal++; }
  return { antal, snit: antal ? Math.round((sum / antal) * 10) / 10 : null, fordeling: f };
}

/** "4,8" – dansk decimaltegn. */
export const snitTekst = (snit: number) => snit.toFixed(1).replace('.', ',');
